import { io } from 'socket.io-client'
import config from '@core/app-config/api-config'

class AdminRealtimeService {
  constructor() {
    this.socket = null
    this.handlers = new Set()
    this.currentScope = null
    this.baseUrl = config.host
  }

  connect() {
    if (this.socket?.connected) return this.socket

    const token = this.resolveAccessToken()
    if (!token) return null

    if (this.socket) {
      this.socket.connect()
      return this.socket
    }

    this.socket = io(this.baseUrl, {
      transports: ['websocket'],
      auth: { token },
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnection: true,
    })
    this.socket.on('admin.realtime.event', evt => {
      this.handlers.forEach(handler => handler(evt))
    })
    this.socket.on('disconnect', () => {
      // Keep socket instance to allow auto-reconnect by socket.io.
    })
    return this.socket
  }

  ensureConnected() {
    const token = this.resolveAccessToken()
    if (!token) return null

    if (!this.socket) {
      return this.connect()
    }

    if (!this.socket.connected) {
      this.socket.auth = { token }
      this.socket.io.opts.extraHeaders = {
        Authorization: `Bearer ${token}`,
      }
      this.socket.connect()
    }

    return this.socket
  }

  subscribe(scope) {
    if (!scope || !scope.startId || scope.distanceId == null) return
    const socket = this.connect()
    if (!socket) return
    if (this.currentScope) {
      socket.emit('admin.realtime.unsubscribe', this.currentScope)
    }
    this.currentScope = { startId: Number(scope.startId), distanceId: Number(scope.distanceId) }
    socket.emit('admin.realtime.subscribe', this.currentScope)
  }

  unsubscribe(scope) {
    const socket = this.socket
    if (!socket) return
    const target = scope || this.currentScope
    if (!target) return
    socket.emit('admin.realtime.unsubscribe', target)
    if (!scope) this.currentScope = null
  }

  onEvent(handler) {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  destroy() {
    this.unsubscribe()
    if (this.socket) {
      this.socket.off('admin.realtime.event')
      this.socket.disconnect()
      this.socket = null
    }
    this.handlers.clear()
  }

  resolveAccessToken() {
    const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken')
    if (token && token.trim()) return token
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      if (userData && typeof userData.accessToken === 'string' && userData.accessToken.trim()) {
        return userData.accessToken
      }
    } catch (e) {
      // ignore invalid localStorage payload
    }
    return null
  }
}

export default new AdminRealtimeService()
